import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link as RouterLink, useParams, useSearchParams } from "react-router-dom";
import Page from "../../core/components/Page";
import HeaderBreadcrumbs from "../../core/components/HeaderBreadcrumbs";
import { OwnerAccessRequestsPanel } from "../../components/access/AccessRequestPanel";
import {
  RecordAccessInviteCard,
  useRecordAccessActions,
} from "../../components/access/RecordAccessManagement";
import {
  accessRequestStatusChipColor,
  accessRequestStatusLabel,
} from "../../components/access/accessLabels";
import { getRecordById, type RecordAccessRequestItem } from "../../services/record.service";
import { PATH_DASHBOARD } from "../../routes/paths";
import { useSnackbar } from "notistack";
import { RowCell } from "../../components/styled";

function collectSharedEmails(record: any): string[] {
  const emails = new Set<string>();
  for (const e of record?.shared_with_emails ?? []) {
    if (e) emails.add(e.toLowerCase());
  }
  const legacy = record?.shared_with_email;
  if (legacy) emails.add(legacy.toLowerCase());
  return Array.from(emails);
}

function RequestHistoryTable({ rows }: { rows: RecordAccessRequestItem[] }) {
  if (rows.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        Geçmiş kayıt yok.
      </Typography>
    );
  }

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>İsteyen</TableCell>
            <TableCell>Durum</TableCell>
            <TableCell>Mesaj</TableCell>
            <TableCell>Tarih</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.id}>
              <RowCell>
                <Typography variant="body2">{row.requester_name ?? row.requester_email}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {row.requester_email}
                </Typography>
              </RowCell>
              <RowCell>
                <Chip
                  label={accessRequestStatusLabel(row.status)}
                  size="small"
                  color={accessRequestStatusChipColor(row.status)}
                />
              </RowCell>
              <RowCell>{row.message ?? "—"}</RowCell>
              <RowCell>
                {new Date(row.created_at).toLocaleString("tr-TR")}
                {row.resolved_at ? (
                  <Typography variant="caption" display="block" color="text.secondary">
                    Çözüm: {new Date(row.resolved_at).toLocaleString("tr-TR")}
                  </Typography>
                ) : null}
              </RowCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default function AccessAuditDetail() {
  const { recordId: recordIdParam } = useParams();
  const [searchParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();
  const recordId = recordIdParam ? Number(recordIdParam) : null;
  const reviewRequestId = searchParams.get("review_request");
  const highlightRequestId = reviewRequestId ? Number(reviewRequestId) : null;
  const pendingPanelRef = useRef<HTMLDivElement>(null);

  const [recordInfo, setRecordInfo] = useState<any>(null);
  const [recordLoading, setRecordLoading] = useState(true);
  const [recordError, setRecordError] = useState(false);

  const access = useRecordAccessActions(recordId, recordId != null && !Number.isNaN(recordId));

  const loadRecord = useCallback(async () => {
    if (!recordId || Number.isNaN(recordId)) {
      setRecordError(true);
      setRecordLoading(false);
      return;
    }
    setRecordLoading(true);
    setRecordError(false);
    try {
      const response = await getRecordById(recordId);
      if (response?.status === 200) {
        setRecordInfo(response.data?.data);
      } else {
        setRecordError(true);
      }
    } catch {
      setRecordError(true);
      enqueueSnackbar("Kayıt yüklenemedi.", { variant: "error" });
    } finally {
      setRecordLoading(false);
    }
  }, [recordId, enqueueSnackbar]);

  useEffect(() => {
    void loadRecord();
  }, [loadRecord]);

  const handleApprove = async (requestId: number) => {
    await access.handleApproveRequest(requestId);
    await loadRecord();
  };

  const sharedEmails = useMemo(() => collectSharedEmails(recordInfo), [recordInfo]);

  const historyRequests = useMemo(
    () => access.accessRequests.filter((r) => r.status !== "Pending"),
    [access.accessRequests],
  );

  const pendingCount = access.accessRequests.filter((r) => r.status === "Pending").length;

  useEffect(() => {
    if (!reviewRequestId || pendingCount === 0) return;
    const timer = window.setTimeout(() => {
      pendingPanelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 300);
    return () => window.clearTimeout(timer);
  }, [reviewRequestId, pendingCount, access.accessRequestsLoaded]);

  const recordName = recordInfo?.record_name ?? `Kayıt #${recordId}`;

  if (recordLoading) {
    return (
      <Page title="Erişim denetimi">
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      </Page>
    );
  }

  if (recordError || !recordId || Number.isNaN(recordId)) {
    return (
      <Page title="Erişim denetimi">
        <HeaderBreadcrumbs
          heading="Kayıt bulunamadı"
          links={[
            { name: "Dashboard", href: PATH_DASHBOARD.general.app },
            { name: "Raporlar", href: PATH_DASHBOARD.reports.root },
            { name: "Erişim denetimi", href: PATH_DASHBOARD.reports.audit },
          ]}
        />
        <Alert severity="error">Bu kayda erişilemiyor veya kayıt mevcut değil.</Alert>
        <Button sx={{ mt: 2 }} component={RouterLink} to={PATH_DASHBOARD.reports.audit}>
          Listeye dön
        </Button>
      </Page>
    );
  }

  return (
    <Page title={`Erişim — ${recordName}`}>
      <HeaderBreadcrumbs
        heading={recordName}
        links={[
          { name: "Dashboard", href: PATH_DASHBOARD.general.app },
          { name: "Raporlar", href: PATH_DASHBOARD.reports.root },
          { name: "Erişim denetimi", href: PATH_DASHBOARD.reports.audit },
          { name: recordName },
        ]}
        action={
          <Button variant="outlined" component={RouterLink} to={`/dashboard/file/${recordId}`}>
            Dosyaya git
          </Button>
        }
      />

      {access.accessRequestsLoaded &&
      reviewRequestId &&
      pendingCount === 0 ? (
        <Alert severity="info" sx={{ mb: 3 }}>
          Bu erişim isteği bulunamadı veya zaten yanıtlandı.
        </Alert>
      ) : null}

      <Card sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Erişimi olanlar
        </Typography>
        {sharedEmails.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Henüz paylaşılan kullanıcı yok. Onaylanan erişim istekleri burada görünür.
          </Typography>
        ) : (
          <Stack direction="row" flexWrap="wrap" gap={1}>
            {sharedEmails.map((email) => (
              <Chip key={email} label={email} size="small" variant="outlined" />
            ))}
          </Stack>
        )}
      </Card>

      {pendingCount > 0 ? (
        <Box ref={pendingPanelRef} id="access-requests-panel">
          <OwnerAccessRequestsPanel
            requests={access.accessRequests}
            highlightRequestId={
              Number.isFinite(highlightRequestId) ? highlightRequestId : null
            }
            actingId={access.actingRequestId}
            bulkActing={access.bulkActing}
            onApprove={handleApprove}
            onReject={access.handleRejectRequest}
            onBulkApprove={access.handleBulkApprove}
            onBulkReject={access.handleBulkReject}
          />
        </Box>
      ) : null}

      {access.inviteStatus ? (
        <RecordAccessInviteCard
          inviteStatus={access.inviteStatus}
          inviteActionLoading={access.inviteActionLoading}
          confirmDialog={access.confirmDialog}
          onSetConfirmDialog={access.setConfirmDialog}
          onConfirmInviteAction={access.handleConfirmInviteAction}
        />
      ) : null}

      <Accordion defaultExpanded={historyRequests.length > 0} sx={{ mb: 3 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">
            İstek geçmişi ({historyRequests.length})
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <RequestHistoryTable rows={historyRequests} />
        </AccordionDetails>
      </Accordion>
    </Page>
  );
}
